import {
  Benchmark,
  BenchMarkOption,
  ProjectRunAnalysisQuestion,
  ProjectSalesforceInfo,
} from '../Types/dealGPT';
import { BenchmarkOptionType } from '../Types/enums';

/**
 * Returns the latest version of each question based on `prettyQuestion`.
 *
 * @param items - The list of questions in a single benchmark category.
 * @returns A deduplicated array with only the latest version of each question.
 */
export function getLatestQuestionVersions(
  items: ProjectRunAnalysisQuestion[]
): ProjectRunAnalysisQuestion[] {
  const latestQuestions = new Map<string, ProjectRunAnalysisQuestion>();

  for (const item of items) {
    if (!item.prettyQuestion) continue;

    const key = item.prettyQuestion;
    const existing = latestQuestions.get(key);

    if (!existing || new Date(item.createdAt) > new Date(existing.createdAt)) {
      latestQuestions.set(key, item);
    }
  }

  return Array.from(latestQuestions.values());
}

export type BenchmarkCategoryGroup = {
  category: string;
  benchmarkQuestions: ProjectRunAnalysisQuestion[];
};

/**
 * Groups a list of project run analysis questions into category groups based on their benchmark category.
 *
 * @param pageQuestions - An array of `ProjectRunAnalysisQuestion` objects to be grouped.
 * @returns An array of `BenchmarkCategoryGroup` objects, where each group represents a benchmark category and questions sorted by display order.
 */
export function getBenchmarkCategoryGroups(
  pageQuestions: ProjectRunAnalysisQuestion[]
): BenchmarkCategoryGroup[] {
  // Group questions by benchmark category
  const groupMap = pageQuestions.reduce((acc, item) => {
    if (item.area && item.prettyQuestion) {
      const group = (acc[item.area] ??= []);
      group.push(item);
    }
    return acc;
  }, {} as Record<string, ProjectRunAnalysisQuestion[]>);

  return Object.entries(groupMap)
    .map(([category, items]) => {
      return {
        category,
        benchmarkQuestions: items.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
      };
    })
    .sort(
      (a, b) =>
        (a.benchmarkQuestions[0]?.displayOrder ?? 0) - (b.benchmarkQuestions[0]?.displayOrder ?? 0)
    );
}

/**
 * Formats the benchmark output string based on the provided benchmark data,
 * benchmark option, and Salesforce project information.
 *
 * @param benchmark - The benchmark data to format. If null or undefined,
 *                    a default message is returned.
 * @param option - The benchmark option containing the type and total value
 *                 used for formatting.
 * @param salesforce - The Salesforce project information, including industry,
 *                     sub-industry, and revenue band, used for contextual replacements.
 * @returns A formatted string representing the benchmark output. If no benchmark
 *          data is available or the total value in the option is zero, a default
 *          message is returned.
 */
export function formatBenchmarkOutput(
  benchmark: Benchmark | null | undefined,
  option: BenchMarkOption,
  salesforce: ProjectSalesforceInfo | null | undefined
): string {
  if (!benchmark || option.total === 0) {
    return 'There is no data available for this benchmark';
  }

  // Calculate percentage
  const percentage = option.total ? Math.round((option.value * 100) / option.total) : 0;

  // Replace the placeholders in the benchmark output format with the actual values
  // Eg: * of {industry}{subindustry} companies {revenue} have deployed privilege access management
  let benchmarkOutput = benchmark.benchmarkOutputFormat.replaceAll('*', `${percentage}%`);

  const replacements: Record<string, Record<string, string>> = {
    [BenchmarkOptionType.Overall]: {
      ' {industry}': '',
      '{subindustry}': '',
      ' {revenue}': '',
    },
    [BenchmarkOptionType.Industry]: {
      '{industry}': `the ${salesforce?.industry ?? ''}`,
      '{subindustry}': '',
      ' {revenue}': '',
    },
    [BenchmarkOptionType.Subindustry]: {
      '{industry}': '',
      '{subindustry}': `the ${salesforce?.subIndustry ?? ''}`,
      ' {revenue}': '',
    },
    [BenchmarkOptionType.Revenue]: {
      '{industry}': `the ${salesforce?.industry ?? ''}`,
      '{subindustry}': '',
      '{revenue}': `with a revenue band: ${salesforce?.revenueBand ?? ''}`,
    },
  };

  Object.entries(replacements[option.type] || {}).forEach(([key, value]) => {
    benchmarkOutput = benchmarkOutput.replaceAll(key, value);
  });

  return benchmarkOutput;
}
